import { forwardRef, useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";

import Dialog from "@mui/material/Dialog";
import DialogContent from "@mui/material/DialogContent";
import IconButton from "@mui/material/IconButton";
import CloseIcon from "@mui/icons-material/Close";

import SoftAvatar from "components/SoftAvatar";
import SoftBox from "components/SoftBox";
import SoftTypography from "components/SoftTypography";
import {
  resolveProfilePic,
  teacherDisplayName,
  teacherInitials,
} from "utils/teacherAvatar";
import { figmaTokens } from "assets/theme/designTokens";

/**
 * Reusable teacher avatar: API `profilePic` (or `ProfilePic`) with initials fallback.
 * When a real image is present, click opens a larger preview dialog.
 */
const TeacherAvatar = forwardRef(function TeacherAvatar(
  {
    profilePic,
    ProfilePic,
    hasProfilePic,
    HasProfilePic,
    name,
    preName,
    teacherName,
    fullName,
    teacher,
    size = "sm",
    bgColor = "info",
    shadow = "none",
    variant = "rounded",
    alt,
    sx,
    preview = true,
    onClick,
    ...rest
  },
  ref
) {
  const identity = teacher || {
    name,
    preName,
    teacherName,
    fullName,
  };

  const resolvedSrc = useMemo(
    () =>
      resolveProfilePic(
        teacher || {
          profilePic,
          ProfilePic,
          hasProfilePic,
          HasProfilePic,
        }
      ),
    [teacher, profilePic, ProfilePic, hasProfilePic, HasProfilePic]
  );

  const [broken, setBroken] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);

  useEffect(() => {
    setBroken(false);
  }, [resolvedSrc]);

  const src = broken ? undefined : resolvedSrc;
  const initials = teacherInitials(identity);
  const label = alt || teacherDisplayName(identity) || "Teacher";
  const canPreview = Boolean(preview && src);

  const handleOpenPreview = (event) => {
    if (onClick) onClick(event);
    if (!canPreview || event.defaultPrevented) return;
    event.preventDefault();
    event.stopPropagation();
    setPreviewOpen(true);
  };

  const handleClosePreview = (event) => {
    event?.stopPropagation?.();
    setPreviewOpen(false);
  };

  return (
    <>
      <SoftAvatar
        ref={ref}
        src={src}
        alt={label}
        size={size}
        bgColor={src ? "transparent" : bgColor}
        shadow={shadow}
        variant={variant}
        role={canPreview ? "button" : undefined}
        tabIndex={canPreview ? 0 : undefined}
        aria-label={canPreview ? `View photo of ${label}` : undefined}
        onClick={canPreview || onClick ? handleOpenPreview : onClick}
        onKeyDown={
          canPreview
            ? (event) => {
                if (event.key === "Enter" || event.key === " ") {
                  handleOpenPreview(event);
                }
              }
            : undefined
        }
        imgProps={{
          loading: "lazy",
          decoding: "async",
          onError: () => setBroken(true),
        }}
        sx={{
          flexShrink: 0,
          color: src ? undefined : "#fff",
          fontWeight: 700,
          cursor: canPreview ? "zoom-in" : undefined,
          transition: "transform 0.15s ease, box-shadow 0.15s ease",
          ...(canPreview
            ? {
                "&:hover": {
                  transform: "scale(1.06)",
                  boxShadow: "0 4px 14px rgba(15, 23, 42, 0.18)",
                },
              }
            : null),
          ...sx,
        }}
        {...rest}
      >
        {initials}
      </SoftAvatar>

      {canPreview ? (
        <Dialog
          open={previewOpen}
          onClose={handleClosePreview}
          maxWidth="sm"
          fullWidth
          onClick={(event) => event.stopPropagation()}
          PaperProps={{
            sx: {
              borderRadius: "18px",
              overflow: "hidden",
              bgcolor: figmaTokens.surface,
              boxShadow: "0 24px 64px rgba(15, 23, 42, 0.28)",
            },
          }}
        >
          <SoftBox
            display="flex"
            alignItems="center"
            justifyContent="space-between"
            px={2.5}
            pt={2}
            pb={1}
          >
            <SoftTypography
              variant="subtitle1"
              sx={{ fontWeight: 700, color: figmaTokens.textPrimary, pr: 1 }}
            >
              {label}
            </SoftTypography>
            <IconButton
              aria-label="Close photo preview"
              onClick={handleClosePreview}
              size="small"
              sx={{ color: figmaTokens.textSecondary }}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          </SoftBox>
          <DialogContent sx={{ pt: 0, px: 2.5, pb: 2.5 }}>
            <SoftBox
              sx={{
                borderRadius: "14px",
                overflow: "hidden",
                bgcolor: "rgba(15, 23, 42, 0.04)",
                border: `1px solid ${figmaTokens.borderSubtle}`,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                minHeight: { xs: 240, sm: 320 },
                maxHeight: { xs: "70vh", sm: "75vh" },
              }}
            >
              <SoftBox
                component="img"
                src={src}
                alt={label}
                sx={{
                  display: "block",
                  width: "100%",
                  maxHeight: { xs: "70vh", sm: "75vh" },
                  objectFit: "contain",
                }}
              />
            </SoftBox>
          </DialogContent>
        </Dialog>
      ) : null}
    </>
  );
});

TeacherAvatar.propTypes = {
  profilePic: PropTypes.string,
  ProfilePic: PropTypes.string,
  hasProfilePic: PropTypes.bool,
  HasProfilePic: PropTypes.bool,
  name: PropTypes.string,
  preName: PropTypes.string,
  teacherName: PropTypes.string,
  fullName: PropTypes.string,
  teacher: PropTypes.object,
  size: PropTypes.oneOf(["xs", "sm", "md", "lg", "xl", "xxl"]),
  bgColor: PropTypes.string,
  shadow: PropTypes.string,
  variant: PropTypes.string,
  alt: PropTypes.string,
  sx: PropTypes.object,
  preview: PropTypes.bool,
  onClick: PropTypes.func,
};

export default TeacherAvatar;
